import {action, computed, observable, runInAction, toJS} from 'mobx';
import GenericFormStore from '../../../stores/FormStore';
import {getImageByInstrument, guid} from '../../../utils/commonUtility';
import {actions, category, events, ProjectAssetsType, RealtimeAction, TrackType, UploadType} from '../../../constants/enums';
import {customEvent} from '../../../utils/analyticsUtil';
import appStore from '../../app/store';
import mainStore from '../../main/store';
import trackStore from '../../main/track/store';
import looperStore from '../../create/looper/store';
import audioStore from '../../../stores/AudioStore';
import {DEFAULT_STEM_LOCATION} from '../../../constants/constants';
import {convertToMp3} from "../../../utils/audioUtility";
import recorderStore from "../../../components/recorder/store";
import wizardStore from "../../studio/store";

class UploadTrackStore extends GenericFormStore {
    @observable isProgress = false;
    @observable form = this.initUploadTrackForm();
    @observable editedTrack = null;
    @observable uploadType = UploadType.UploadFile;
    @observable isAddToLooperAfterUpload = true;
    @observable isSubmitted = false;
    @observable isUploading = false;
    @observable previewError = false;
    @observable file = {};

    // @observable attachmentsChanged = false;

    initUploadTrackForm() {
        return {
            fields: {
                trackName: {
                    value: '',
                    error: '',
                    rule: 'required',
                },
                file: {
                    value: '',
                    error: '',
                    rule: 'required',
                },
                attachments: {
                    value: [],
                    error: '',
                    rule: 'array'
                },
                bpm: {
                    value: '',
                    error: '',
                    rule: 'required|numeric',
                },
                key: {
                    value: '',
                    error: '',
                    rule: 'string',
                },
                tsBeatsPerBar: {
                    value: '',
                    error: '',
                    rule: 'numeric',
                },
                tsBeatsMeter: {
                    value: '',
                    error: '',
                    rule: 'numeric',
                },
                scale: {
                    value: '',
                    error: '',
                    rule: 'string',
                },
                instruments: {
                    value: [],
                    rule: 'array',
                    error: '',
                },
                genre: {
                    value: [],
                    rule: 'array',
                    error: '',
                },
                addToFeed: {
                    value: false,
                    rule: 'boolean',
                    error: ''
                },
                forceMono: {
                    value: false,
                    rule: 'boolean',
                    error: ''
                },
                splitAudio: {
                    value: false,
                    rule: 'boolean',
                    error: ''
                }
            },
            meta: {
                isValid: false,
                error: null,
            },
        };
    }

    @action
    init() {
        this.form = this.initUploadTrackForm();
        this.previewError = false;
        this.file = null;
    }

    @action
    setPreviewError(val) {
        this.previewError = val;
    }

    processChipItems = (items) => {
        return items.map((item) => {
            return {name: item};
        });
    };
    /**
     * Process the track to send to the server
     * @param fields {Object}  the form fields
     * @param file  {Object}    the file data added
     * @returns {{trackId: string, fileId: string, trackName: *, instruments, genre, trackOrigFileName, timestamp: number}}
     */
    processTrack = (fields, file, previewError) => {
        return {
            fileId: guid(),
            name: fields.trackName.value,
            instruments: this.processChipItems(toJS(fields.instruments.value)),
            genre: this.processChipItems(toJS(fields.genre.value)),
            originalFileName: file.name,
            userBPM: parseInt(fields.bpm.value, 10),
            type: TrackType.Track,
            key: fields.key.value,
            scale: fields.scale.value,
            tsBeatsPerBar: fields.tsBeatsPerBar.value,
            tsBeatsMeter: fields.tsBeatsMeter.value,
            addToFeed: fields.addToFeed.value,
            uploadType: this.uploadType,
            latencyFixed: false,
            forceMono: fields.forceMono.value,
            splitAudio: fields.splitAudio.value,
            previewError: previewError,
            attachments: this.processAttachments(fields.attachments.value)
        };
    };

    processAttachment = (attachment) => {
        return {id: attachment.id || guid(), name: attachment.name, type: attachment.type, file: attachment}
    };

    processAttachments = (attachments) => {
        return attachments.map(this.processAttachment);
    };

    /**
     * handle upload track submit
     */
    @action submitTrack = async () => {
        try {
            looperStore.sendActionUpdate(RealtimeAction.AudioUpload, {isDone: false});

            const fields = Object.assign({}, this.form.fields);
            let track = this.processTrack(fields, this.file, this.previewError);
            const file = this.file;
            const duration = this.duration;
            track = await trackStore.uploadTrack(track, file, duration, false, true, fields.addToFeed.value);
            trackStore.addTrackToUpload(track);
            runInAction(() => {
                this.form = this.initUploadTrackForm();
            })
            return track;
        } catch (err) {
            throw err;
        } finally {
            setTimeout(() => {
                looperStore.sendActionUpdate(RealtimeAction.AudioUpload, {isDone: true});
            }, 1000);
            this.file = null;
        }
    };

    /**
     * Set new chip for genre or instruments
     * @param chip  {String}    the new chip value
     * @param formItem  {String}    The chip type
     */
    @action setChip = (chip, formItem) => {
        this.form.fields[formItem].value = [chip];
        this.onChange(formItem, this.form.fields[formItem].value);
    };
    /**
     * Remove a chip from either genre or instruments
     * @param chip  {String}    the new chip value
     * @param index {number}    The chip index in the array
     * @param formItem  {String}    The chip type
     */
    @action removeChip = (chip, index, formItem) => {
        this.form.fields[formItem].value = [];
        const chipRemoved = [];
        // const chipRemoved = this.form.fields[formItem].value.remove(chip);
        if (!chipRemoved) {
            const chipIndex = this.form.fields[formItem].value.findIndex(chipObj => chipObj.name === chip);
            if (chipIndex > -1) {
                this.form.fields[formItem].value.splice(chipIndex, 1);
            }
        }

        this.onChange(formItem, this.form.fields[formItem].value);
    };

    getNameFromFile = () => {
        try {
            let {name} = this.file;
            const lastPoint = name.lastIndexOf('.');
            if (lastPoint >= 0) {
                name = name.substring(0, lastPoint);
            }
            return name;
        } catch (err) {
            return null;
        }
    };

    @action setTrackName = (name) => {
        this.onChange('trackName', name);
    };

    /**
     * Handle file change saving the file data to this.file
     * then continue the form validation
     * @param field {String}    The form field
     * @param value {String}    The file name
     * @param target    {Object}    The DOM element
     */
    @action onFileChange = (field, value, target) => {
        switch (field) {
            case 'file':
                if (target.files) {
                    const [file] = target.files;
                    this.file = file;
                    this.duration = target.duration;
                } else {
                    const [file] = target;
                    this.file = file;
                    this.duration = null;
                    this.setTrackName(this.getNameFromFile());
                }
                break;
            default:
        }
        this.onChange(field, value);
    };

    @action onAttachmentChange = (field, value, target) => {
        switch (field) {
            case 'attachments':
                this.form.fields.attachments.value = this.form.fields.attachments.value.concat(target);
                break;
            default:
        }
    };

    @action onAttachmentDelete = (fileName) => {
        const fileIndex = this.form.fields.attachments.value.findIndex(p => p.name === fileName);
        this.form.fields.attachments.value.splice(fileIndex, 1);
        // this.attachmentsChanged = true;
    };


    @action clearFileInput = () => {
        this.file = null;
        this.onChange('file', '');
        this.setTrackName('');
    };

    @action onClickRec = async () => {
    };

    @action fromTrackToForm = (track) => {
        const currentTrack = toJS(track);
        if (currentTrack.name) this.form.fields.trackName.value = currentTrack.name;
        if (currentTrack.bpm) this.form.fields.bpm.value = Math.round(currentTrack.bpm);
        if (currentTrack.key) this.form.fields.key.value = currentTrack.key;
        if (currentTrack.scale) this.form.fields.scale.value = currentTrack.scale;
        if (currentTrack.tsBeatsPerBar) this.form.fields.tsBeatsPerBar.value = currentTrack.tsBeatsPerBar;
        if (currentTrack.tsBeatsMeter) this.form.fields.tsBeatsMeter.value = currentTrack.tsBeatsMeter;
        if (currentTrack.instruments) this.form.fields.instruments.value = currentTrack.instruments.map(o => o.name);
        if (currentTrack.genre) this.form.fields.genre.value = currentTrack.genre.map(o => o.name);
        if (currentTrack.attachments) this.form.fields.attachments.value = currentTrack.attachments || [];
        this.form.fields.file = {
            value: '',
            error: '',
            rule: '',
        };
        this.editedTrack = track;
    };

    @action setBpm = (bpm) => {
        this.form.fields.bpm.value = bpm;
    };

    @action saveEditedTrack = async () => {
        const {fields} = this.form;
        const track = {
            ...this.editedTrack,
            name: fields.trackName.value,
            instruments: this.processChipItems(toJS(fields.instruments.value)),
            genre: this.processChipItems(toJS(fields.genre.value)),
            userBPM: parseInt(fields.bpm.value, 10),
            bpm: parseInt(fields.bpm.value, 10),
            type: TrackType.Track,
            key: fields.key.value,
            scale: fields.scale.value,
            tsBeatsPerBar: fields.tsBeatsPerBar.value,
            tsBeatsMeter: fields.tsBeatsMeter.value,
            addToFeed: fields.addToFeed.value,
            attachments: this.processAttachments(fields.attachments.value),
        };
        if (!track.backgroundImage || track.backgroundImage.indexOf(DEFAULT_STEM_LOCATION) >= 0) {
            track.backgroundImage = getImageByInstrument(track.instruments);
        }
        await trackStore.saveTrack(track);
        return track;
    };

    @action setUploadType = (type) => {
        this.uploadType = type;
    };

    @action setIsAddToLooperAfterUpload = (isAdd) => {
        this.isAddToLooperAfterUpload = isAdd;
    };

    @action handleSubmitTrack = async () => {
        if (!this.validateForm()) {
            return false;
        }
        this.isUploading = true;
        mainStore.toggleProgressLoader(true);
        try {
            if (this.uploadType === UploadType.EditFile) {
                const track = await this.saveEditedTrack();
                await trackStore.storeAttachments(track);
                looperStore.updateTrackInLooper(track);
                appStore.createAlertFromMessageId('success', 'notations.successful', 2000);
                customEvent(events.CustomEvent, {category: category.Upload, action: actions.EditTrack});
            } else {
                if (this.uploadType === UploadType.Record) {
                    const name = `recording-${guid()}`;
                    console.log('convert to mp3');
                    const file = await convertToMp3(this.form.fields.file.value);
                    console.log('done converting to mp3');
                    this.onFileChange('file', name, {
                        files: [file],
                        duration: recorderStore.recording ? recorderStore.recording.duration : null
                    });
                }
                const track = await this.submitTrack();
                if (this.isAddToLooperAfterUpload) {
                    await looperStore.addToLooper(track);
                    wizardStore.addStepByType(ProjectAssetsType.Audio);
                }
            }
        } catch (err) {
            console.log(err);
            audioStore.deleteAudio();
        } finally {
            mainStore.toggleProgressLoader(false);
            runInAction(() => {
                this.isUploading = false;
            });
            return true;
        }
    };

    validateForm = () => {
        const uploadTrackFields = toJS(this.form.fields);
        Object.keys(uploadTrackFields).forEach((key) => {
            this.onBlur(key);
        });
        return this.form.meta.isValid;
    };

    @action initFormByPreviousTrack = (track) => {
        const {bpm, key, tsBeatsPerBar, tsBeatsMeter, scale, genre} = this.form.fields;
        bpm.value = track.bpm < 0 ? '' : Math.round(track.bpm);
        key.value = track.key;
        tsBeatsPerBar.value = track.tsBeatsPerBar;
        tsBeatsMeter.value = track.tsBeatsMeter;
        scale.value = track.scale;
        genre.value = (track.genre || []).map(i => i.name);
    }

    @computed get isFormReady() {
        const trackNameSet = this.form.fields.trackName.value.trim().length !== 0;
        console.log(trackNameSet, this.uploadType === UploadType.EditFile, this.uploadType !== UploadType.Record && this.isFileReady, this.uploadType === UploadType.Record && recorderStore.recording)
        return trackNameSet && (this.uploadType === UploadType.EditFile || (this.uploadType !== UploadType.Record && this.isFileReady) || (this.uploadType === UploadType.Record && recorderStore.recording));
    }

    @computed get isFileReady() {
        return this.file && this.file.size > 0;
    }
}

const uploadTrackStore = new UploadTrackStore();
export default uploadTrackStore;

